import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { getGameFromSlug } from "../../../api/user";
import DashboardHeader from "../../../components/Other/DashboardHeader";
import LeagueShell from "./LeagueShell";
import LeagueColumns from "./LeagueColumns";
import SearchPage from "../../SearchPage";

// /:slug/watchlist — the Watchlist tab INSIDE a league.
//
// Client Rev 3 reported this tab whiting out the app. The route genuinely did not
// exist, so the router matched nothing and rendered nothing; the stop-gap was to
// send the tab to the app's own watchlist instead. Client Rev 4: "Watchlist no
// longer white-outs, but it is currently pointing to the main QUSHi site instead
// of staying in the league." Right — that stop-gap walked the member out of the
// league, which is the thing the client has asked us to stop doing everywhere
// else (League Details, profile pictures). So the route now exists.
//
// The body is the SAME watchlist the site itself uses — SearchPage, the screen
// behind /search — rendered `embedded` so it contributes the watchlist alone and
// the league shell supplies the header, the container and the league navigation.
// That is the identical arrangement Portfolio and the feed already use here: a
// league is a child of the app, so it gets the app's real screens rather than
// cut-down copies that drift.
//
// Scope (client Rev 2, global rule 6: "All portfolio, watchlist, trade, feed, and
// leaderboard data must remain scoped to the current league"). The lists shown
// here are now the member's lists IN THIS LEAGUE, not their main-site ones. A
// watchlist row carries an optional game_id; the API layer reads the league slug
// off the URL and sends it with every request that reads or writes a member's own
// lists, so no screen has to be told which scope it is in. See the interceptor in
// api/user/index.js.
//
// The curated market categories (The Core, US Stocks, Crypto) are deliberately
// NOT scoped — they are the same lists everywhere on QUSHi and are nobody's
// personal data. So a member entering a league for the first time still sees a
// populated screen; it is only their OWN lists that start empty here.
const LeagueWatchlist = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  // AuthLayout passes [sidebarToggle]; DashboardHeader needs it for the mobile
  // rail toggle. Read defensively — the Contest layout passes an object instead.
  const outletContext = useOutletContext();
  const sidebarToggle = Array.isArray(outletContext)
    ? outletContext[0]
    : outletContext?.sidebarToggle;
  const [game, setGame] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setResolved(false);
    (async () => {
      try {
        const res = await getGameFromSlug({ slug });
        // A bad slug answers with HTTP 200 + success:false, so read the body.
        const data = res?.data?.success === false ? null : res?.data?.data;
        if (!cancelled) setGame(data?.id ? data : null);
      } catch (e) {
        console.error("League watchlist lookup failed:", e);
        if (!cancelled) setGame(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!resolved) return;
    if (!game) {
      navigate("/contest", { replace: true });
      return;
    }
    // A league's own screens are for MEMBERS.
    if (!game.isMember) navigate(`/contest/${game.vanity_url}`, { replace: true });
  }, [resolved, game, navigate]);

  if (!resolved) {
    return (
      <>
      <DashboardHeader toggle={sidebarToggle} />
      <div className="mt-16 md:mt-24 pb-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="h-36 md:h-44 rounded-2xl bg-[#F3F4F6] animate-pulse" />
        </div>
      </div>
      </>
    );
  }
  if (!game || !game.isMember) return null;

  return (
    <>
      {/* The app header. Every other league screen renders it (GamePortfolio,
          GameLeaderboard, GameCharts all do) and LeagueShell assumes one is above
          it — its default `topOffset` of mt-16/md:mt-24 exists purely to clear it.
          This screen was built without one, and because `SearchPage embedded`
          suppresses the header it would otherwise render itself, the result was a
          page with NO header at all: no centred QUSHi logo and no avatar.
          Client Rev 2, global League Play rule 1 is the opposite: "Keep the QUSHi
          logo centered and the user avatar in the top left unless noted
          otherwise." */}
      <DashboardHeader toggle={sidebarToggle} />
      {/* `standalone`: this route is mounted at /:slug/watchlist, outside the
          /contest tree, so there is no PageContainer above it to clear the fixed
          256px desktop sidebar. Measured at 1440px, the identity block's left edge
          was 104px — under the sidebar — against 288px on the league Portfolio.
          Same defect, same cause and same one-line fix as the league Leaderboard
          the client reported in Rev 3. Desktop only; mobile is untouched. */}
      <LeagueShell game={game} active="watchlist" compact standalone /* Watchlist (client Rev 2, Watchlist 2 & 3): eyebrow + league title only */>
        {/* Same shared shell as Home and Portfolio — client Rev 2: "One consistent
            main column width across Home, Portfolio, and Watchlist. Sticky right
            rail beside that column." Desktop only; on mobile the grid collapses to
            one column and no rail renders. */}
        <LeagueColumns game={game} className="mt-2">
          <SearchPage embedded />
        </LeagueColumns>
      </LeagueShell>
    </>
  );
};

export default LeagueWatchlist;
